import { ContentfulImage, ContentLayout, TemplateWrapper } from "@2u-landing-pages/components";
import { Bento, Form } from "@2u-landing-pages/templates";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Container, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import Image from "next/image";
import React from "react";
import { renderEmbeddedEntry, renderMark, renderText } from "../../../../utils/contentful-richtext";
import type { CustomCSSProperties, HeroProps } from "../../Hero.types";

const componentsMap = {
  bento: Bento,
  form: Form,
};

// @ts-ignore
import styles from "./Default.module.scss";

export const Default: React.FC<HeroProps> = ({
  inspector,
  main_content,
  right_slot,
  bottom_slot,
  background_image,
  programLogo,
  divider_color,
  phoneNumber,
  parseMetadata,
  isLoading,
}) => {
  const courseName = main_content?.fields?.eyebrow || "";
  const mainContent = {
    ...main_content,
    fields: {
      ...main_content?.fields,
      eyebrow: undefined, // remove the eyebrow which is displayed in the top slot.
    },
  };
  const hasBottomSlot = bottom_slot ? Boolean(componentsMap[bottom_slot?.sys?.contentType?.sys?.id || ""]) : false;
  const style: CustomCSSProperties = {};

  if (divider_color) {
    style["--hero-divider-color"] = `var(${divider_color})`;
  }

  const backgroundImageUrl = background_image?.fields?.file?.url;

  return (
    <TemplateWrapper
      className={cn(styles.heroTemplateWrapper, {
        [styles.heroWrapper]: hasBottomSlot,
      })}
      style={style}
    >
      <div
        className={cn(styles.heroTopWrapper, {
          [styles.bottomOverlap]: hasBottomSlot,
        })}
      >
        {backgroundImageUrl && (
          <Image
            src={backgroundImageUrl}
            alt=""
            sizes="100vw"
            loader={({ src, width }) => {
              const quality = width < 1024 ? 60 : 75;
              const adjustedWidth = Math.max(width - 240, 240);
              const height = Math.round(adjustedWidth / 1.85); // Calculate height for 1.85:1 ratio
              return `https:${src}?w=${adjustedWidth}&h=${height}&q=${quality}&fm=avif&fit=fill`;
            }}
            priority
            fill
            className={styles.heroTopBackgroundImage}
          />
        )}
        <Container
          className={styles.heroTop}
          flex
          direction="column"
          lg={{ direction: "row", alignItems: "flex-start" }}
        >
          <div className={cn(styles.mainSlot, { [styles.mainSlotSolo]: !right_slot })}>
            <ContentLayout
              topSlot={
                <div
                  className={cn(styles.courseInfo, {
                    [styles.courseDivider]: Boolean(courseName),
                  })}
                >
                  <ContentfulImage image={programLogo} className={styles.logo} width={400} loading="eager" />
                  {courseName &&
                    typeof courseName === "object" &&
                    documentToReactComponents(courseName, {
                      renderMark,
                      renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <h1 className={styles.eyebrowWrapper}>
                            <Subhead
                              variant="subhead-md"
                              type="span"
                              className={cn(styles.primaryText, styles.courseName)}
                              style={
                                main_content?.fields?.eyebrow_color
                                  ? { color: `var(${main_content.fields.eyebrow_color})` }
                                  : {}
                              }
                              {...inspector({
                                entryId: main_content?.sys?.id || "",
                                fieldId: "eyebrow",
                              })}
                            >
                              {children}
                            </Subhead>
                          </h1>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(node, "Hero", courseName, phoneNumber, parseMetadata),
                      },
                    })}
                </div>
              }
              content={mainContent as HeroProps["main_content"]}
              heading={{
                variant: courseName ? "heading-xl" : "heading-lg",
                className: styles.mainContentHeading,
              }}
              btnPlacement="center"
              phoneNumber={phoneNumber}
              parseMetadata={parseMetadata}
            />
          </div>
          {right_slot && componentsMap[right_slot?.sys?.contentType?.sys?.id] && (
            <div className={styles.rightSlot}>
              {React.createElement(componentsMap[right_slot?.sys?.contentType?.sys?.id], {
                ...right_slot?.fields,
                embedded: true,
                phoneNumber,
                parseMetadata,
                isLoading,
              })}
            </div>
          )}
        </Container>
      </div>
      {hasBottomSlot && bottom_slot && (
        <div className={styles.heroBottomWrapper}>
          {React.createElement(componentsMap[bottom_slot?.sys?.contentType?.sys?.id], {
            ...bottom_slot?.fields,
            embedded: true,
            className: styles.heroBottom,
            phoneNumber,
            parseMetadata,
            inspector,
          })}
        </div>
      )}
    </TemplateWrapper>
  );
};
